import { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import useDataProvider, { useQuery } from '@concepta/react-data-provider';
import TestimonialCard from './TestimonialCard';
import { UserTestimonialsInterface } from 'ks-common';
import useIntersectionObserver from 'app/hooks/useIntersectionObserver';
import { toast } from 'react-toastify';
import { Section } from 'app/components/StaticPages';

const NUMBER_OF_CARDS = 12;

const Testimonials = () => {
  const { get } = useDataProvider();

  const [testimonials, setTestimonials] = useState<UserTestimonialsInterface[]>(
    [],
  );

  const getTestimonials = () => {
    return get({
      uri: '/user-testimonials',
      queryParams: {
        sort: 'CreatedDate,DESC',
        limit: NUMBER_OF_CARDS,
        offset: testimonials.length,
      },
    });
  };

  const { data, execute, isPending } = useQuery(getTestimonials, false, {
    onError: error => {
      // Todo: Fix error response in rockets
      const errorRes = error as unknown as {
        response: { data: { message: string } };
      };

      toast.error(errorRes?.response?.data?.message || error?.message);
    },
  });

  // Undefined before the first fetch, so the initial load can run
  const hasMore = !data?.data || data.data.length === NUMBER_OF_CARDS;

  useEffect(() => {
    if (data?.data) {
      // Offset pagination over CreatedDate DESC shifts when rows are inserted
      // mid-browse, so repeated TIDs across pages are expected — drop them.
      setTestimonials(prev => {
        const seen = new Set(prev.map(t => t.TID));
        return [
          ...prev,
          ...data.data.filter(
            (t: UserTestimonialsInterface) => !seen.has(t.TID),
          ),
        ];
      });
    }
  }, [data]);

  const loadMore = () => {
    if (!isPending && hasMore) execute();
  };

  // Load the first page on mount; the observer below fetches subsequent pages
  useEffect(() => {
    loadMore();
  }, []);

  const loaderRef = useRef(null);
  const entry = useIntersectionObserver(loaderRef, { threshold: 0.5 });

  useEffect(() => {
    if (entry?.isIntersecting) loadMore();
  }, [entry]);

  return (
    <Section
      innerSx={{
        pt: { xs: 5, md: 7 },
        pb: { xs: 6, md: 9 },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
        }}
      >
        {testimonials?.map(testimonial => (
          <TestimonialCard key={testimonial.TID} testimonial={testimonial} />
        ))}
      </Box>

      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        my={4}
        ref={loaderRef}
      >
        {isPending && <CircularProgress />}
      </Box>
    </Section>
  );
};

export default Testimonials;
